import { useEffect, useRef, useState } from 'react'

export type Gesture = 'idle' | 'recording' | 'cancel'

// 按住说话胶囊。仅 enabled(聆听中)可按:按下立即开 ASR;松手(指在按钮内)→ 发送;
// 上滑拖出按钮再松手 → 取消。用 Pointer Events + setPointerCapture,手指划出按钮仍能收到 move/up。
// 音波不画在按钮里——上报手势状态给父级,由上方状态行统一显示(和 VAD 模式同一处)。
export function HoldToTalkButton({
    enabled,
    onStart,
    onStop,
    onCancel,
    onGesture
}: {
    enabled: boolean
    onStart: () => void
    onStop: () => void
    onCancel: () => void
    onGesture?: (g: Gesture) => void
}) {
    const [gesture, setGesture] = useState<Gesture>('idle')
    const btnRef = useRef<HTMLButtonElement>(null)
    const activePointer = useRef<number | null>(null)
    const gestureRef = useRef<Gesture>('idle') // pointerup 时读最新值,避开 setState 异步

    const setG = (g: Gesture) => {
        gestureRef.current = g
        setGesture(g)
        onGesture?.(g)
    }

    // 按住中被禁用(典型:另一根手指按了静音 → status 离开 listening)→ 机器已 pause asr,
    // 这里把手势复位、丢弃这次按住,避免卡在录音态。activePointer 清空后,后续 pointerup 自然成 no-op。
    useEffect(() => {
        if (enabled || activePointer.current === null) return
        try {
            btnRef.current?.releasePointerCapture(activePointer.current)
        } catch {
            /* ignore */
        }
        activePointer.current = null
        setG('idle')
        // setG 不入依赖:稳定闭包,只关心 enabled 翻转
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    // 命中测试:指尖是否还在按钮内。上滑取消 = 拖到按钮上沿之上;两侧/下给一点宽容。
    const inBounds = (x: number, y: number): boolean => {
        const el = btnRef.current
        if (!el) return false
        const r = el.getBoundingClientRect()
        const SLOP = 16
        return y >= r.top && y <= r.bottom + SLOP && x >= r.left - SLOP && x <= r.right + SLOP
    }

    const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (!enabled || activePointer.current !== null) return
        e.preventDefault()
        activePointer.current = e.pointerId
        try {
            btnRef.current?.setPointerCapture(e.pointerId)
        } catch {
            /* ignore */
        }
        setG('recording')
        onStart()
    }

    const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (activePointer.current !== e.pointerId) return
        setG(inBounds(e.clientX, e.clientY) ? 'recording' : 'cancel')
    }

    const finish = (e: React.PointerEvent<HTMLButtonElement>, forceCancel: boolean) => {
        if (activePointer.current !== e.pointerId) return
        try {
            btnRef.current?.releasePointerCapture(e.pointerId)
        } catch {
            /* ignore */
        }
        activePointer.current = null
        const cancelled = forceCancel || gestureRef.current === 'cancel'
        setG('idle')
        if (cancelled) onCancel()
        else onStop()
    }

    const recording = gesture === 'recording'
    const cancel = gesture === 'cancel'

    return (
        <button
            ref={btnRef}
            type="button"
            disabled={!enabled}
            aria-label="按住说话"
            style={{ touchAction: 'none' }}
            onContextMenu={(e) => e.preventDefault()}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={(e) => finish(e, false)}
            onPointerCancel={(e) => finish(e, true)}
            className={`flex h-12 flex-1 select-none items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition ${
                !enabled
                    ? 'glass bg-bg-secondary text-text-disabled opacity-50'
                    : cancel
                      ? 'bg-destructive text-white shadow-lg shadow-destructive/30'
                      : recording
                        ? 'bg-accent text-white shadow-lg shadow-accent/30'
                        : 'glass bg-bg-secondary text-text-primary active:scale-[0.98]'
            }`}
        >
            {cancel ? (
                <span>松开取消</span>
            ) : recording ? (
                <span>松开发送 上滑取消</span>
            ) : (
                // 同时点明两种模式,消除"普通聆听 vs 长按"的心智负担:加粗"按住"/"直接"
                <span>
                    <span className="font-bold">按住</span> 或 <span className="font-bold">直接</span> 说话
                </span>
            )}
        </button>
    )
}
